import { useState, useEffect, useMemo } from "react";
import { Container, Row, Col } from "react-grid-system";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import {
  updateProductFields,
  updateProductStatus,
  AddProductToCategory,
  archiveProduct,
} from "../../../server/Product";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Input, { Select } from "../../containers/InputForm";
import ProductEditPerloed from "./productEditPerloed";
import {
  LinkedProducts,
  LinkedProductsRemove,
} from "../../../server/LinkedProducts";
import { getDictByKind, getAllCategory } from "../../../server/Category";
import { findCategoryBySlug } from "../products/useCategoryUtils";
import * as actionType from "../../../store/action";
import showAlert, {
  showAlertWithConfirmCancel,
} from "../../../server/ShowAlert";
import { useNavigate } from "react-router-dom";
import useFamilyProductUpdate, {
  applySequentially,
} from "../productPage/useFamilyProductUpdate";
import { buildNestedFieldUpdate } from "../../../utils/productExpirationDisplay";
import {
  PRODUCT_STATUS_OPTIONS,
  getProductStatusUpdateBlockers,
  resolveProductCategorySlug,
} from "../../../utils/productStatus";

/** Product-only fields: never offered to sibling family products. */
const PRODUCT_SPECIFIC_FIELDS = new Set(["name", "gtin"]);

const toNestedProductPatch = (change) => {
  if (!change || !Object.keys(change).length) return null;
  const patch = {};
  for (const [key, value] of Object.entries(change)) {
    if (!key.includes(".")) {
      patch[key] = value;
      continue;
    }
    const nested = buildNestedFieldUpdate(key, value);
    for (const [topKey, topVal] of Object.entries(nested)) {
      if (
        topVal &&
        typeof topVal === "object" &&
        !Array.isArray(topVal) &&
        patch[topKey] &&
        typeof patch[topKey] === "object" &&
        !Array.isArray(patch[topKey])
      ) {
        patch[topKey] = { ...patch[topKey], ...topVal };
      } else {
        patch[topKey] = topVal;
      }
    }
  }
  return patch;
};

const isDirtyField = (dirtyFields, name) => {
  if (!dirtyFields || !name) return false;
  if (dirtyFields[name]) return true;
  const parts = String(name)
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".");
  let current = dirtyFields;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return false;
    current = current[part];
  }
  return Boolean(current);
};

const schema = yup
  .object({
    name: yup.string().required("שם המוצר חובה"),
    gtin: yup
      .string()
      .required("חובה להכניס ברקוד")
      .test("len", (x) => x.length >= 5 && x.length <= 15),
    description: yup.string().max(35, "תיאור חייב להיות עד 35 תווים"),
  })
  .required();

const getKashrutFeatureSelectValue = (storedFeature) => {
  if (!storedFeature) return "";
  if (typeof storedFeature === "object") {
    return storedFeature.id || storedFeature.name || "";
  }
  return storedFeature;
};

const normalizeProductFormValues = (product, productStatus = "") => {
  const kosherSupervision = product?.kosherSupervision?.map((entry, index) => {
    if (index !== 0 || entry?.kashrutFeature == null) return entry;
    return {
      ...entry,
      kashrutFeature: getKashrutFeatureSelectValue(entry.kashrutFeature),
    };
  });

  return {
    ...product,
    ...(kosherSupervision ? { kosherSupervision } : {}),
    verifyAge: Boolean(product?.verifyAge),
    status: productStatus,
  };
};

const ProductEditForm = ({ setPendingScrollSlug, onSaved }) => {
  const [catsOptions, setCatsOptions] = useState([]);
  const [alterProds, setAlterProds] = useState([]);
  const [parallelProds, setParallelProds] = useState([]);
  const [parentCategory, setParentCategory] = useState(null);
  const [subBrand, setSubBrand] = useState([]);
  const [kashrut, setKashrut] = useState([]);
  const [kashrutFeature, setKashrutFeature] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { requestUpdate, familyDialog } = useFamilyProductUpdate();

  const categoriesState = useSelector((state) => state.category.categories);
  const product = useSelector((state) => state.selectedProduct.product);
  const products = useSelector((state) => state.selectedProduct.products);
  const stationId = useSelector((state) => state.user.stationId);
  const productCategories = useSelector(
    (state) => state.selectedProduct.productCategories,
  );

  const {
    register,
    formState: { errors, dirtyFields, isDirty },
    reset,
    handleSubmit,
    setValue,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: normalizeProductFormValues(product),
    resolver: yupResolver(schema),
  });

  const kashrutTypeOptions = [
    { Id: "חלבי", Name: "חלבי" },
    { Id: "בשרי", Name: "בשרי" },
    { Id: "פרווה", Name: "פרווה" },
    { Id: "לא מוגדר", Name: "לא מוגדר" },
  ];

  // Select value is the dictionary id; label stays the display name.
  // Legacy / removed dict entries are injected so existing product data stays visible.
  const storedKashrutFeature = product?.kosherSupervision?.[0]?.kashrutFeature;
  const storedKashrutFeatureId =
    getKashrutFeatureSelectValue(storedKashrutFeature);
  const storedKashrutFeatureName =
    storedKashrutFeature &&
    (typeof storedKashrutFeature === "object"
      ? storedKashrutFeature.name || storedKashrutFeature.id
      : storedKashrutFeature);

  const kashrutFeatureOptions = useMemo(() => {
    const options = kashrutFeature.map((item) => ({
      Id: item.id,
      Name: item.name,
    }));
    if (
      storedKashrutFeatureId &&
      !options.some(
        (opt) =>
          opt.Id === storedKashrutFeatureId ||
          opt.Name === storedKashrutFeatureName,
      )
    ) {
      options.unshift({
        Id: storedKashrutFeatureId,
        Name: storedKashrutFeatureName || storedKashrutFeatureId,
      });
    }
    return options;
  }, [kashrutFeature, storedKashrutFeatureId, storedKashrutFeatureName]);

  const unitOfMeasureOptions = [
    { Id: "גרם", Name: "גרם" },
    { Id: "יחידה", Name: "יחידה" },
    { Id: "יחידות", Name: "יחידות" },
    { Id: "ליטר", Name: "ליטר" },
    { Id: 'מ"ל', Name: 'מ"ל' },
    { Id: "מטר", Name: "מטר" },
    { Id: 'ק"ג', Name: 'ק"ג' },
  ];

  const statusOptions = PRODUCT_STATUS_OPTIONS.map((option) => ({
    Id: option.id,
    Name: option.name,
  }));

  useEffect(() => {
    if (subBrand.length && product?.subBrand?.id)
      setValue("subBrand", product.subBrand?.id);
    if (
      kashrut.length &&
      product?.kosherSupervision?.[0]?.supervisionAgency?.id
    )
      setValue(
        "kashrut",
        product.kosherSupervision?.[0]?.supervisionAgency?.id,
      );
    const storedFeature = product?.kosherSupervision?.[0]?.kashrutFeature;
    if (storedFeature) {
      const storedId =
        typeof storedFeature === "object"
          ? storedFeature.id || storedFeature.name
          : storedFeature;
      const storedName =
        typeof storedFeature === "object"
          ? storedFeature.name || storedFeature.id
          : storedFeature;
      const match =
        kashrutFeature.find((item) => item.id === storedId) ||
        kashrutFeature.find((item) => item.name === storedName);
      if (match?.id) {
        setValue("kosherSupervision[0].kashrutFeature", match.id);
      } else if (storedId) {
        setValue("kosherSupervision[0].kashrutFeature", storedId);
      }
    }
  }, [product, subBrand, kashrut, kashrutFeature, setValue]);

  const editProductId = product?.productId ?? product?.id ?? null;

  // Full form + category cascade reset only when navigating to another product.
  // Local category updates must not wipe catLevel* or force re-init.
  useEffect(() => {
    if (!product || !editProductId) return;
    setIsInitialized(false);
    setCatsOptions([]);
    setParentCategory(null);
    reset(
      normalizeProductFormValues(
        product,
        productCategories?.[0]?.productStatus || "",
      ),
    );
    // productCategories is read for initial status only; do not re-run on category edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProductId, reset]);

  // Keep status select in sync when productCategories status changes (e.g. status toggle)
  // without resetting category fields.
  useEffect(() => {
    if (!isInitialized) return;
    const status = productCategories?.[0]?.productStatus;
    if (
      status === "ACTIVE" ||
      status === "ACTIVE_UNTIL_OOS" ||
      status === "INACTIVE"
    ) {
      setValue("status", status);
    }
  }, [productCategories, isInitialized, setValue]);

  useEffect(() => {
    if (!stationId) return;
    dispatch(getAllCategory(stationId, null, 5, true, true));
  }, [dispatch, stationId]);

  useEffect(() => {
    const fetchFromDict = (kind, setKind) => {
      getDictByKind(kind)
        .then((res) => setKind(res))
        .catch((error) => console.error("Error fetching dictionary:", error));
    };
    fetchFromDict("sub-brand", setSubBrand);
    fetchFromDict("kashrut", setKashrut);
    fetchFromDict("kashrut-feature", setKashrutFeature);
  }, []);

  const showArchiveMessage = (type, text) => {
    showAlert({ msgTitle: text, msgType: type });
  };

  const archiveCurrentProduct = () => {
    if (!product?.productId) return;

    showAlertWithConfirmCancel({
      msgTitle: "האם להעביר את המוצר לארכיון?",
      confirmText: "אישור",
      cancelText: "ביטול",

      onConfirm: async () => {
        try {
          await archiveProduct(product.productId, stationId);

          showArchiveMessage("success", "המוצר הועבר לארכיון בהצלחה");

          setTimeout(() => {
            const hierarchy = productCategories?.[0]?.categoryHierarchy ?? [];
            const reversedNav = [...hierarchy].reverse();

            if (reversedNav.length > 0) {
              const firstCategory = reversedNav[0];

              if (reversedNav.length > 1) {
                const secondCategory = reversedNav.at(-1);
                setPendingScrollSlug?.(secondCategory.slug);
              }

              navigate(`/category/${firstCategory.slug}`);
            } else {
              navigate("/");
            }
          }, 900);
        } catch (error) {
          console.error("Archive product failed:", error);

          showArchiveMessage("error", "אירעה שגיאה בהעברת המוצר לארכיון");
        }
      },
    });
  };

  const resetFormToProduct = () => {
    reset(
      normalizeProductFormValues(
        product,
        productCategories?.[0]?.productStatus || "",
      ),
    );
  };

  const selectProductLink = (currProd, chosenProd, action) => {
    const val = {
      ProductMkt: currProd,
      SubProductMkt: chosenProd,
      TypeLinked: action,
    };
    LinkedProducts(val)
      .then(() => {
        const match = products.find((prods) => prods.Mkt === chosenProd);
        switch (action) {
          case "1":
            const alterProdsEdit = [...alterProds];
            alterProdsEdit.push(match);
            setAlterProds(alterProdsEdit);
            break;
          case "2":
            const parallelProdsEdit = [...parallelProds];
            parallelProdsEdit.push(match);
            setParallelProds(parallelProdsEdit);
            break;
          default:
            break;
        }
      })
      .catch(() => console.log("error"));
  };

  const removeLinkage = (ProductMkt, SubProductMkt, TypeLinked) => {
    const val = { TypeLinked, ProductMkt, SubProductMkt };
    LinkedProductsRemove(val)
      .then(() => {
        if (TypeLinked === "1") {
          const nextAlter = alterProds.filter(
            (item) => item.ProductMkt !== ProductMkt,
          );
          setAlterProds(nextAlter);
        } else if (TypeLinked === "2") {
          const nextParallel = parallelProds.filter(
            (item) => item.ProductMkt !== ProductMkt,
          );
          setParallelProds(nextParallel);
        }
      })
      .catch(() => console.log("error"));
  };

  const buildKosherSupervision = (overrides = {}) => {
    const existing = product?.kosherSupervision?.[0] ?? {};
    return [{ ...existing, ...overrides }];
  };

  const buildDirtyProductChanges = (values) => {
    const change = {};
    const kosherOverrides = {};
    const take = (fieldId, assign) => {
      if (!isDirtyField(dirtyFields, fieldId)) return;
      assign();
    };

    take("name", () => {
      change.name = values.name;
    });
    take("gtin", () => {
      change.gtin = values.gtin;
    });
    take("SubMkt", () => {
      change.SubMkt = values.SubMkt;
    });
    take("description", () => {
      change.description = values.description;
    });
    take("weightJump", () => {
      change.weightJump = values.weightJump;
    });
    take("weightDetails.netContent", () => {
      change["weightDetails.netContent"] = values.weightDetails?.netContent;
    });
    take("weightDetails.unitOfMeasure", () => {
      change["weightDetails.unitOfMeasure"] =
        values.weightDetails?.unitOfMeasure;
    });
    take("weightDetails.grossWeight", () => {
      change["weightDetails.grossWeight"] = values.weightDetails?.grossWeight;
    });
    take("logisticsDetails.packageQuantity", () => {
      change["logisticsDetails.packageQuantity"] =
        values.logisticsDetails?.packageQuantity;
    });
    take("vatExempt", () => {
      change.vatExempt = Boolean(values.vatExempt);
    });
    take("verifyAge", () => {
      change.verifyAge = Boolean(values.verifyAge);
    });
    take("SupervisedPrice", () => {
      change.SupervisedPrice = Boolean(values.SupervisedPrice);
    });
    take("weightDetails.weighedProduct", () => {
      change["weightDetails.weighedProduct"] = Boolean(
        values.weightDetails?.weighedProduct,
      );
    });
    take("depositItem", () => {
      change.depositItem = Boolean(values.depositItem);
    });
    take("expiredManagement", () => {
      change.expiredManagement = Boolean(values.expiredManagement);
    });
    take("viewBrand", () => {
      change.viewBrand = Boolean(values.viewBrand);
    });
    take("showOutOfStock", () => {
      change.showOutOfStock = Boolean(values.showOutOfStock);
    });
    take("allowOrderWhenOos", () => {
      change.allowOrderWhenOos = Boolean(values.allowOrderWhenOos);
    });
    take("logisticsDetails.hasExpirationDate", () => {
      Object.assign(
        change,
        buildNestedFieldUpdate(
          "logisticsDetails.hasExpirationDate",
          Boolean(values.logisticsDetails?.hasExpirationDate),
        ),
      );
    });
    take("subBrand", () => {
      const selected = subBrand.find((item) => item.id === values.subBrand);
      if (selected) {
        change.subBrand = { id: selected.id, name: selected.name };
      }
    });
    take("kashrut", () => {
      const selected = kashrut.find((item) => item.id === values.kashrut) || {};
      kosherOverrides.supervisionAgency = (({ id, name, img, tenantKind }) => ({
        id,
        name,
        img,
        tenantKind,
      }))(selected);
    });
    take("kosherSupervision[0].kashrutFeature", () => {
      const value = values.kosherSupervision?.[0]?.kashrutFeature;
      const fromDict = kashrutFeature.find((item) => item.id === value);
      kosherOverrides.kashrutFeature = fromDict
        ? { id: fromDict.id, name: fromDict.name }
        : value
          ? { id: value, name: value }
          : value;
    });
    take("kosherSupervision[0].classification", () => {
      kosherOverrides.classification =
        values.kosherSupervision?.[0]?.classification;
    });

    if (Object.keys(kosherOverrides).length > 0) {
      change.kosherSupervision = buildKosherSupervision(kosherOverrides);
    }

    return change;
  };

  const splitProductChanges = (change) => {
    const productSpecific = {};
    const familyShared = { ...change };
    for (const field of PRODUCT_SPECIFIC_FIELDS) {
      if (field in familyShared) {
        productSpecific[field] = familyShared[field];
        delete familyShared[field];
      }
    }
    return { productSpecific, familyShared };
  };

  const saveProductChanges = handleSubmit(async (values) => {
    if (!product?.productId || !isDirty) return;

    const previousStatus = productCategories?.[0]?.productStatus || "";
    const statusDirty = isDirtyField(dirtyFields, "status");
    const status = values.status;
    const needsStatus = statusDirty && status !== previousStatus;

    if (needsStatus) {
      const productForStatus = { ...product, categories: productCategories };
      const missing = getProductStatusUpdateBlockers(
        productForStatus,
        undefined,
        status,
      );
      if (missing.length) {
        showAlert({
          msgTitle: `לא ניתן לעדכן סטטוס לפני הגדרת: ${missing.join(", ")}`,
          msgType: "error",
        });
        setValue("status", previousStatus, { shouldDirty: false });
        return;
      }
      if (!resolveProductCategorySlug(productForStatus)) {
        showAlert({
          msgTitle: "לא נמצאה קטגוריה לעדכון הסטטוס",
          msgType: "error",
        });
        setValue("status", previousStatus, { shouldDirty: false });
        return;
      }
    }

    const change = buildDirtyProductChanges(values);
    const { productSpecific, familyShared } = splitProductChanges(change);
    const hasProductSpecific = Object.keys(productSpecific).length > 0;
    const hasFamilyShared = Object.keys(familyShared).length > 0;

    if (!hasProductSpecific && !hasFamilyShared && !needsStatus) {
      const categoryDirty = [1, 2, 3, 4].some((i) =>
        isDirtyField(dirtyFields, `catLevel${i}`),
      );
      if (!categoryDirty) return;
      reset(values);
      showAlert({ msgTitle: "השינויים נשמרו בהצלחה", msgType: "success" });
      onSaved?.([product], { mainProductId: product.productId });
      return;
    }

    const nextProduct = {
      ...product,
      ...values,
      ...(familyShared.subBrand ? { subBrand: familyShared.subBrand } : {}),
      ...(familyShared.kosherSupervision
        ? { kosherSupervision: familyShared.kosherSupervision }
        : {}),
      ...productSpecific,
    };

    const persist = async (productsToChange) => {
      setIsSaving(true);
      try {
        if (hasProductSpecific) {
          await updateProductFields(
            stationId,
            product.productId,
            productSpecific,
          );
        }

        if (hasFamilyShared) {
          await applySequentially(productsToChange, async (prod) => {
            await updateProductFields(stationId, prod.productId, familyShared);
          });
        }

        if (needsStatus) {
          const categorySlug = resolveProductCategorySlug({
            ...product,
            categories: productCategories,
          });
          await applySequentially(productsToChange, async (prod) => {
            const productId =
              prod.productId ?? prod.product_id ?? prod.id ?? product.productId;
            await updateProductStatus(
              productId,
              categorySlug,
              status,
              stationId,
            );
          });
          if (productCategories?.[0]) {
            dispatch({
              type: actionType.SET_PRODUCT_CATEGORIES,
              data: [
                { ...productCategories[0], productStatus: status },
                ...productCategories.slice(1),
              ],
            });
          }
        }

        dispatch({
          type: actionType.SET_SELCTED_PRODUCT,
          data: { Product: nextProduct },
        });
        reset(
          normalizeProductFormValues(
            nextProduct,
            needsStatus ? status : previousStatus,
          ),
        );
        showAlert({ msgTitle: "השינויים נשמרו בהצלחה", msgType: "success" });
        onSaved?.(productsToChange, {
          familyPatch: toNestedProductPatch(familyShared),
          productSpecificPatch: toNestedProductPatch(productSpecific),
          mainProductId: product.productId,
          status: needsStatus ? status : undefined,
        });
        return true;
      } catch {
        showAlert({
          msgTitle: "אירעה שגיאה בשמירת השינויים",
          msgType: "error",
        });
        resetFormToProduct();
        return false;
      } finally {
        setIsSaving(false);
      }
    };

    // Family popup only when shared/family fields (or status) changed — never for name/barcode alone.
    if (hasFamilyShared || needsStatus) {
      requestUpdate(persist, resetFormToProduct);
      return;
    }

    await persist([product]);
  });

  useEffect(() => {
    if (isInitialized) return;
    if (!categoriesState.length) return;
    if (product === undefined) return;

    const hierarchy = productCategories?.[0]?.categoryHierarchy || [];
    const rootToLeaf = [...hierarchy].reverse();

    if (rootToLeaf.length > 1) {
      const treeNotReady = rootToLeaf
        .slice(0, -1)
        .some(
          (cat) =>
            !findCategoryBySlug(categoriesState, cat.slug)?.children?.length,
        );
      if (treeNotReady) return;
    }

    rootToLeaf.forEach((cat, index) => {
      setValue(`catLevel${index + 1}`, cat.slug);
    });

    if (hierarchy[0]?.isFamily && hierarchy.length < 4) {
      setValue("catLevel4", hierarchy[0].slug);
    }

    setParentCategory(hierarchy[0]?.slug ?? null);

    const newCatsOptions = [categoriesState];

    rootToLeaf.forEach((cat) => {
      const found = findCategoryBySlug(categoriesState, cat.slug);
      if (found?.children?.length > 0) {
        newCatsOptions.push(found.children);
      }
    });

    if (hierarchy[0]?.isFamily && hierarchy.length < 4) {
      const parent = findCategoryBySlug(
        categoriesState,
        hierarchy[0].parentSlug,
      );
      if (parent?.children?.length > 0) {
        while (newCatsOptions.length < 4) {
          newCatsOptions.push([]);
        }
        newCatsOptions[3] = parent.children;
      }
    }

    setCatsOptions(newCatsOptions);
    setIsInitialized(true);
  }, [product, productCategories, categoriesState, isInitialized, setValue]);

  const buildHierarchyNode = (cat, parentSlug) => ({
    slug: cat.slug,
    name: cat.name,
    parentSlug: cat.parentSlug ?? parentSlug ?? null,
    isFamily: Boolean(cat.isFamily),
  });

  const resolveCategoryNode = (slug) => {
    if (!slug) return null;
    return (
      findCategoryBySlug(categoriesState, slug) ||
      catsOptions
        .flatMap((levelOptions) => levelOptions || [])
        .find((cat) => String(cat.slug) === String(slug)) ||
      null
    );
  };

  /** Root-to-leaf nodes for levels 1 .. upToLevelExclusive-1 from the form. */
  const getSelectedPathNodes = (upToLevelExclusive) => {
    const nodes = [];
    const existingRootToLeaf = [
      ...(productCategories?.[0]?.categoryHierarchy || []),
    ].reverse();

    for (let i = 1; i < upToLevelExclusive; i++) {
      const slug = watch(`catLevel${i}`);
      if (!slug) break;

      const resolved = resolveCategoryNode(slug);
      if (resolved) {
        nodes.push(
          buildHierarchyNode(resolved, nodes[nodes.length - 1]?.slug ?? null),
        );
        continue;
      }

      const fallback = existingRootToLeaf[i - 1];
      if (fallback && String(fallback.slug) === String(slug)) {
        nodes.push(fallback);
      }
    }
    return nodes;
  };

  const applyLocalCategoryPath = ({ level, selectedCategory, cleared }) => {
    const rootToLeaf = getSelectedPathNodes(level);
    const resolved = selectedCategory
      ? resolveCategoryNode(selectedCategory.slug) || selectedCategory
      : null;

    if (!cleared && resolved) {
      rootToLeaf.push(
        buildHierarchyNode(
          resolved,
          rootToLeaf[rootToLeaf.length - 1]?.slug ?? null,
        ),
      );
    }

    setCatsOptions((prev) => {
      const newCatsOptions = prev.slice(0, level);
      if (!newCatsOptions[0]?.length) {
        newCatsOptions[0] = categoriesState;
      }
      if (!cleared && resolved?.children?.length) {
        newCatsOptions[level] = resolved.children;
      }
      return newCatsOptions;
    });

    if (!cleared && resolved) {
      setValue(`catLevel${level}`, resolved.slug);
    }
    for (let i = cleared ? level : level + 1; i <= 4; i++) {
      setValue(`catLevel${i}`, null);
    }

    const leafFirst = [...rootToLeaf].reverse();
    const productStatus = productCategories?.[0]?.productStatus || "INACTIVE";

    dispatch({
      type: actionType.SET_PRODUCT_CATEGORIES,
      data: [
        {
          categoryHierarchy: leafFirst,
          productStatus,
        },
      ],
    });
    setParentCategory(leafFirst[0]?.slug ?? null);
  };

  const tryUpdateCategory = ({ target: { id, value } }) => {
    handleSubmit();
    if (errors[id]) return;

    const level = parseInt(id.split("catLevel")[1], 10);
    if (!Number.isFinite(level) || level < 1) return;

    const cleared = value === "";
    const previousSlugAtLevel = watch(`catLevel${level}`);

    const effectiveValue = cleared
      ? productCategories?.[0]?.categoryHierarchy.find(
          (c) => String(c.slug) === String(previousSlugAtLevel),
        )?.parentSlug
      : value;

    if (!cleared && !effectiveValue) return;
    if (cleared && !effectiveValue) return;

    const selectedCategory = cleared
      ? null
      : resolveCategoryNode(effectiveValue) ||
        catsOptions[level - 1]?.find(
          (cat) => String(cat.slug) === String(effectiveValue),
        );

    if (!cleared && !selectedCategory) return;

    const previousCatsOptions = catsOptions;
    const previousParentCategory = parentCategory;
    const previousProductCategories = productCategories;
    const previousLevelValues = [1, 2, 3, 4].map((i) => watch(`catLevel${i}`));

    const revertCategoryUi = () => {
      setCatsOptions(previousCatsOptions);
      setParentCategory(previousParentCategory);
      previousLevelValues.forEach((slug, index) => {
        setValue(`catLevel${index + 1}`, slug ?? null);
      });
      dispatch({
        type: actionType.SET_PRODUCT_CATEGORIES,
        data: previousProductCategories,
      });
    };

    // Reveal next levels immediately; API confirms in the background.
    applyLocalCategoryPath({ level, selectedCategory, cleared });

    if (!previousParentCategory) {
      dispatch(
        AddProductToCategory(effectiveValue, product.productId, stationId),
      ).catch(() => {
        revertCategoryUi();
        showAlert({
          msgTitle: "אירעה שגיאה בשיוך המוצר למחלקה",
          msgType: "error",
        });
      });
      return;
    }

    updateProductFields(stationId, product.productId, {
      fromSlug: previousParentCategory,
      toSlug: effectiveValue,
    }).catch((e) => {
      console.log("error", e);
      revertCategoryUi();
      showAlert({
        msgTitle: "אירעה שגיאה בעדכון הקטגוריה",
        msgType: "error",
      });
    });
  };

  const getCategoryLevelDisplayName = (level) => {
    const hierarchy = productCategories?.[0]?.categoryHierarchy || [];
    const rootToLeaf = [...hierarchy].reverse();

    if (level === 4 && hierarchy[0]?.isFamily && hierarchy.length < 4) {
      return hierarchy[0]?.name || "";
    }

    return rootToLeaf[level - 1]?.name || "";
  };

  const { onChange: registerStatusChange, ...statusRegisterRest } =
    register("status");

  return (
    <form className="product-details-edit" onSubmit={saveProductChanges}>
      <Container>
        <Row>
          <InputCol
            sm={3}
            name="name"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            type="textarea"
            label="שם המוצר"
          />
          <InputCol
            sm={3}
            name="gtin"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            label="ברקוד"
            type="text"
          />
          <InputCol
            sm={2}
            name="SubMkt"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            type="text"
            label="מקט יצרן"
          />
          <Col sm={4}>
            <div className="status-and-archive">
              <div style={{ flex: 1 }}>
                <Select
                  name="status"
                  errors={errors}
                  {...statusRegisterRest}
                  options={statusOptions}
                  label="סטטוס"
                  onChange={registerStatusChange}
                  value={watch("status")}
                  clearable={false}
                  dirtyFields={dirtyFields}
                />
              </div>

              <button
                type="button"
                onClick={archiveCurrentProduct}
                className="ui-btn-brand archive-product-btn"
              >
                העבר לארכיון
              </button>
            </div>
          </Col>
        </Row>

        <Row>
          <InputCol
            sm={4}
            name="description"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            type="textarea"
            label="תיאור קצר"
          />
          <SelectCol
            sm={4}
            name="kashrut"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            options={kashrut}
            label="כשרות"
            value={watch("kashrut")}
          />
          {catsOptions[0] && (
            <SelectCol
              sm={4}
              name="catLevel1"
              errors={errors}
              register={register}
              onChange={tryUpdateCategory}
              options={catsOptions[0]}
              label="מחלקה"
              value={watch("catLevel1")}
              displayLabel={getCategoryLevelDisplayName(1)}
              clearable={false}
            />
          )}
        </Row>

        <Row>
          <InputCol
            sm={2}
            name="weightDetails.netContent"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            type="text"
            label="משקל"
          />
          <SelectCol
            sm={2}
            name="weightDetails.unitOfMeasure"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            options={unitOfMeasureOptions}
            label="יח׳ משקל"
            value={watch("weightDetails.unitOfMeasure")}
          />
          <SelectCol
            sm={4}
            name="kosherSupervision[0].kashrutFeature"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            options={kashrutFeatureOptions}
            label="כשרויות נוספות"
            value={watch("kosherSupervision[0].kashrutFeature")}
          />
          {catsOptions[1]?.length && (
            <SelectCol
              sm={4}
              name="catLevel2"
              errors={errors}
              onChange={tryUpdateCategory}
              register={register}
              options={catsOptions[1]}
              label="קבוצה"
              value={watch("catLevel2")}
              displayLabel={getCategoryLevelDisplayName(2)}
            />
          )}
        </Row>

        <Row>
          <InputCol
            sm={2}
            name="weightJump"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            type="text"
            label="קפיצת משקל"
          />
          <InputCol
            sm={3}
            name="weightDetails.grossWeight"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            type="text"
            label="סטיית משקל ברוטו"
          />
          <SelectCol
            sm={3}
            name="kosherSupervision[0].classification"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            options={kashrutTypeOptions}
            label="חלבי / בשרי"
            value={watch("kosherSupervision[0].classification")}
          />
          {catsOptions[2]?.length > 0 && (
            <SelectCol
              sm={4}
              name="catLevel3"
              errors={errors}
              onChange={tryUpdateCategory}
              register={register}
              options={catsOptions[2]}
              label="תת קבוצה"
              value={watch("catLevel3")}
              displayLabel={getCategoryLevelDisplayName(3)}
            />
          )}
        </Row>

        <Row>
          <Col sm={4}>
            <ProductEditPerloed
              label="מוצר מקביל"
              arrShow={parallelProds}
              onSelectProduct={(value) =>
                selectProductLink(product.Mkt, value, "2")
              }
              products={products}
              removeLinkage={(value) => removeLinkage(product.Mkt, value, "2")}
            />
          </Col>

          <SelectCol
            sm={4}
            name="subBrand"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            options={subBrand}
            label="מותג"
            value={watch("subBrand")}
          />
          {catsOptions[3]?.length > 0 && (
            <SelectCol
              sm={4}
              name="catLevel4"
              errors={errors}
              onChange={tryUpdateCategory}
              register={register}
              options={catsOptions[3]}
              label="מקבץ"
              value={watch("catLevel4")}
              displayLabel={getCategoryLevelDisplayName(4)}
            />
          )}
        </Row>

        <Row>
          <Col sm={4}>
            <ProductEditPerloed
              label="מוצר חלופי"
              arrShow={alterProds}
              onSelectProduct={(value) =>
                selectProductLink(product.Mkt, value, "1")
              }
              products={products}
              removeLinkage={(value) => removeLinkage(product.Mkt, value, "1")}
            />
          </Col>

          <InputCol
            sm={2}
            name="logisticsDetails.packageQuantity"
            errors={errors}
            dirtyFields={dirtyFields}
            register={register}
            type="text"
            label="כמות למארז"
          />
        </Row>

        <div className="check-box">
          <div className="checkbox-style">
            <Input
              name="vatExempt"
              errors={errors}
              dirtyFields={dirtyFields}
              register={register}
              type="checkbox"
              label='פטור מע"מ'
            />
          </div>
          <div className="checkbox-style">
            <Input
              name="verifyAge"
              errors={errors}
              dirtyFields={dirtyFields}
              register={register}
              type="checkbox"
              label="דורש אישור 18+"
              checked={Boolean(watch("verifyAge"))}
            />
          </div>
          <div className="checkbox-style">
            <Input
              name="SupervisedPrice"
              errors={errors}
              dirtyFields={dirtyFields}
              register={register}
              type="checkbox"
              label="בפיקוח"
            />
          </div>
          <div className="checkbox-style">
            <Input
              name="weightDetails.weighedProduct"
              errors={errors}
              dirtyFields={dirtyFields}
              register={register}
              type="checkbox"
              label="מוצר שקיל"
            />
          </div>
          <div className="checkbox-style">
            <Input
              name="depositItem"
              errors={errors}
              dirtyFields={dirtyFields}
              register={register}
              type="checkbox"
              label="פריט פקדון"
            />
          </div>
          <div className="checkbox-style">
            <Input
              name="expiredManagement"
              errors={errors}
              dirtyFields={dirtyFields}
              register={register}
              type="checkbox"
              label="ניהול תוקף"
            />
          </div>
          <div className="checkbox-style">
            <Input
              name="logisticsDetails.hasExpirationDate"
              errors={errors}
              dirtyFields={dirtyFields}
              register={register}
              type="checkbox"
              label="הצג תוקף"
            />
          </div>
          <div className="checkbox-style">
            <Input
              name="viewBrand"
              errors={errors}
              dirtyFields={dirtyFields}
              register={register}
              type="checkbox"
              label="הצג מותג"
            />
          </div>
          <div className="checkbox-style">
            <Input
              name="showOutOfStock"
              errors={errors}
              dirtyFields={dirtyFields}
              register={register}
              type="checkbox"
              label="העלמת תמונה"
            />
          </div>
          <div className="checkbox-style">
            <Input
              name="allowOrderWhenOos"
              errors={errors}
              dirtyFields={dirtyFields}
              register={register}
              type="checkbox"
              label="ניתן להזמין גם כשחסר במלאי"
            />
          </div>
        </div>

        <div className="product-details-edit__actions">
          <button
            type="submit"
            className="ui-btn-brand product-details-edit__save-btn"
            disabled={!isDirty || isSaving}
          >
            {isSaving ? "שומר..." : "שמור שינויים"}
          </button>
        </div>
      </Container>
      {familyDialog}
    </form>
  );
};

const InputCol = ({
  name,
  errors,
  label,
  type,
  register,
  sm,
  onBlur,
  dirtyFields,
}) => {
  return (
    <Col sm={sm}>
      <Input
        name={name}
        errors={errors}
        onBlur={onBlur}
        dirtyFields={dirtyFields}
        register={register}
        label={label}
        type={type}
      />
    </Col>
  );
};

const SelectCol = ({
  name,
  errors,
  label,
  register,
  sm,
  options,
  onChange,
  value,
  displayLabel,
  clearable = true,
  dirtyFields,
}) => {
  const { onChange: registerOnChange, ...registerRest } = register(name);

  return (
    <Col sm={sm}>
      <Select
        name={name}
        errors={errors}
        {...registerRest}
        options={options}
        label={label}
        onChange={(e) => {
          registerOnChange?.(e);
          onChange?.(e);
        }}
        value={value}
        displayLabel={displayLabel}
        clearable={clearable}
        dirtyFields={dirtyFields}
      />
    </Col>
  );
};

export default ProductEditForm;
